import { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions,
} from "@mui/material";
import { SoftButton, SoftRow, cls, useDialogPaperSx } from "../../hatim/soft/SoftKit";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useRemoveHatimUserMutation } from "../../../api/hatimManagementApi";
import { useToast } from "../../../components/ui/Toast";
import { assignedTotal, memberAssigned, remainingToAssign } from "../../../lib/hatimAssignment";

function initials(p) {
  const a = (p.name || "").trim()[0] || "?";
  const b = (p.surname || "").trim()[0] || "";
  return (a + b).toUpperCase();
}

function getInfo(u) {
  return {
    id: u.user?._id || u.user || u._id,
    name: u.user?.name || u.name,
    surname: u.user?.surname || u.surname,
    email: u.user?.email || u.email,
    assignedAmount: u.assignedAmount,
    assignedPageRanges: u.assignedPageRanges,
  };
}

function Stat({ label, value, tone }) {
  return (
    <div className="flex flex-col min-w-[72px]">
      <span className={`text-[11px] uppercase tracking-wide ${cls.muted}`}>{label}</span>
      <span className={`text-lg font-Default ${tone || cls.title}`}>{value}</span>
    </div>
  );
}

/**
 * Atanan üyeler: üstte toplam / atanan / kalan / tekrar özeti, altta üye
 * satırları. Düzenle ve Hatimden çıkar butonları ikon + metin.
 */
export default function HatimTemplateMembersList({
  hatimTemplateId,
  users,
  type,
  isRange,
  totalAmount,
  repeatDuration,
  onEdit,
}) {
  const { t } = useScriptMode();
  const toast = useToast();
  const [removeUser, { isLoading }] = useRemoveHatimUserMutation();
  const [pending, setPending] = useState(null);
  const paperSx = useDialogPaperSx();

  const raw = Array.isArray(users) ? users : [];
  const list = raw.map(getInfo);
  const unit = isRange ? t("hatim_progress_unit_pages") : t("hatim_progress_unit_count");
  const total = Math.max(0, Number(totalAmount) || 0);
  const assigned = assignedTotal(raw, type);
  const remaining = remainingToAssign(raw, type, total);

  const handleConfirm = async () => {
    if (!pending) return;
    try {
      await removeUser({ hatimTemplateId, email: pending.email }).unwrap();
      toast.success(t("hatim_mgmt_user_removed"));
    } catch (err) {
      const msg = err?.message || err?.data?.message || t("hatim_mgmt_action_error");
      toast.error(msg);
    } finally {
      setPending(null);
    }
  };

  return (
    <>
      {/* Üstte sabit açık özet şeridi */}
      <div className={`sticky top-0 z-[1] flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3 ${cls.summary}`}>
        <Stat label={t("hatim_mgmt_members_total")} value={`${total} ${unit}`} />
        <Stat label={t("hatim_mgmt_members_assigned")} value={`${assigned} ${unit}`} />
        <Stat
          label={t("hatim_mgmt_members_remaining")}
          value={`${remaining} ${unit}`}
          tone={remaining === 0 && total > 0 ? cls.accent : cls.warning}
        />
        {repeatDuration ? (
          <Stat label={t("hatim_mgmt_members_repeat")} value={String(repeatDuration)} />
        ) : null}
        <Stat label={t("hatim_mgmt_card_users")} value={String(list.length)} />
      </div>

      {list.length === 0 ? (
        <p className={`text-sm px-4 py-4 ${cls.muted}`}>{t("hatim_mgmt_users_empty")}</p>
      ) : (
        <ul className="tk-stagger">
          {list.map((u) => (
            <SoftRow
              key={u.id || u.email}
              avatar={initials(u)}
              title={`${u.name || ""} ${u.surname || ""}`.trim()}
              meta={`${u.email}${
                isRange && (u.assignedPageRanges || []).length > 0
                  ? ` · ${(u.assignedPageRanges || []).map((r) => `${r.startPage}–${r.endPage}`).join(", ")}`
                  : ""
              }`}
              right={
                <div className="flex items-center gap-2 shrink-0">
                  <span
                    className={`px-2.5 py-1 rounded-full text-sm font-Medium ${cls.chip}`}
                    aria-label={t("hatim_mgmt_members_assigned")}
                  >
                    {memberAssigned(u, type)} {unit}
                  </span>
                  <SoftButton
                    tone="soft"
                    size="small"
                    startIcon={<EditOutlinedIcon fontSize="small" />}
                    onClick={() => onEdit(u)}
                    disabled={isLoading}
                  >
                    {t("hatim_mgmt_user_edit")}
                  </SoftButton>
                  <SoftButton
                    tone="cancel"
                    size="small"
                    startIcon={<DeleteOutlineIcon fontSize="small" />}
                    onClick={() => setPending(u)}
                    disabled={isLoading}
                  >
                    {t("hatim_mgmt_user_remove")}
                  </SoftButton>
                </div>
              }
            />
          ))}
        </ul>
      )}

      <Dialog open={!!pending} onClose={() => setPending(null)} maxWidth="xs" fullWidth PaperProps={{ sx: paperSx }}>
        <DialogTitle>{t("hatim_mgmt_remove_confirm_title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("hatim_mgmt_remove_confirm_body")}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SoftButton tone="cancel" onClick={() => setPending(null)} disabled={isLoading}>
            {t("hatim_mgmt_remove_confirm_cancel")}
          </SoftButton>
          <SoftButton
            tone="danger"
            startIcon={<DeleteOutlineIcon fontSize="small" />}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {t("hatim_mgmt_user_remove")}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
